import { ReadAcrossDataType } from "../../page/IndividualReadAcrossPage";
import { ReactComponent as CameraIcon } from '../../../../assets/Icons/adornments/Camera.svg';
import RADetailsModal from "../../../../components/Popups/RADetailsModal";

import { toPng } from 'html-to-image';

import { 
    ReactFlow, 
    Controls, 
    // Background, 
    Node, 
    Edge,
    useReactFlow,
    ControlButton,
    // getNodesBounds,
    // getViewportForBounds,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useCallback, useEffect, useState, Dispatch, SetStateAction } from "react";
import CenterNode from "./CenterNode";
import SurroundingNode from "./SurroundingNode";
import CustomEdge from "./CustomEdge";
import { convertNumberToDecimal } from "../../../../utils/formattingFns";

const nodeTypes = {
    centerNode: CenterNode,
    surroundingNode: SurroundingNode
}

const edgeTypes = {
    customEdge: CustomEdge,
}

export default function RAFlowChart({
    readacrossData,
    selectedFilter,
    setSelectedFilter,
    searchInput,
    setSearchInput,
}: {
    readacrossData: ReadAcrossDataType[],
    selectedFilter: string,
    setSelectedFilter: Dispatch<SetStateAction<string>>,
    searchInput: string,
    setSearchInput: Dispatch<SetStateAction<string>>,
}) {
    const [ nodes, setNodes ] = useState<Node[]>([]);
    const [ edges, setEdges ] = useState<Edge[]>([]);
    const [modalData, setModalData] = useState<ReadAcrossDataType | null>(null);
    const [modalIndex, setModalIndex] = useState<number | null>(null);
    const [modalOpen, setModalOpen] = useState(false);

    const { 
        fitView, 
        // getNodes 
    } = useReactFlow();

    useEffect(() => {
        const handleResize = () => fitView();

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [fitView]);


    useEffect(() => {
        if (readacrossData.length > 0) {
            const centerX = 300;
            const centerY = 200;
            const nodeRadius = 80;
            const layoutRadius = 300;

            let parent: ReadAcrossDataType | undefined;
            let analogs: ReadAcrossDataType[] = [];

            readacrossData.forEach(data => {
                if (data.tanimoto === 1) {
                    parent = data;
                } else {
                    analogs.push(data);
                }
            });

            if (parent && analogs.length > 0) {
                const surroundingNodes: Node[] = analogs.map((analog, i) => {
                    const { smiles, chemical_name, tanimoto } = analog;

                    const angle = (2 * Math.PI * i) / analogs.length;
                    return {
                        id: `node-${i}`,
                        type: 'surroundingNode',
                        position: {
                            x: centerX + layoutRadius * Math.cos(angle),
                            y: centerY + layoutRadius * Math.sin(angle),
                        },
                        data: {
                            label: chemical_name,
                            smiles,
                            angle,
                            radius: nodeRadius,
                            tanimoto: convertNumberToDecimal(tanimoto),
                            raData: analog,
                            index: i+1,
                            onOpenModal: (rsdata: ReadAcrossDataType, index: number) => {
                                setModalData(rsdata);
                                setModalIndex(index);
                                setModalOpen(true);
                            },
                        },
                    }
                });

                const centerNode: Node = {
                    id: 'center',
                    type: 'centerNode',
                    position: { x: centerX, y: centerY },
                    data: {
                        label: parent.chemical_name,
                        smiles: parent.smiles,
                        radius: nodeRadius,
                        angles: surroundingNodes.map((n, i) => {
                            const dx = n.position.x - centerX;
                            const dy = n.position.y - centerY;
                            return Math.atan2(dy, dx);
                        }),
                        raData: parent,
                        index: 0,
                        onOpenModal: (rsdata: ReadAcrossDataType, index: number) => {
                            setModalData(rsdata);
                            setModalIndex(index);
                            setModalOpen(true);
                        },
                    },
                };

                const nodeEdges: Edge[] = surroundingNodes.map((n, i) => ({
                    id: `center-${n.id}`,
                    source: 'center',
                    sourceHandle: `to-${i}`,
                    target: n.id,
                    type: 'customEdge',
                    label: n.data.tanimoto as number,
                }));

                setNodes(surroundingNodes.concat(centerNode));
                setEdges(nodeEdges);
            }
        }
    }, [readacrossData]);


    const onDownloadPNG = useCallback(async () => {

        const reactFlowViewport = document.querySelector('.react-flow__viewport');

        if (!reactFlowViewport) return;

        try {
            const dataUrl = await toPng(reactFlowViewport as HTMLElement, { cacheBust: true, backgroundColor: '#ffffff', });

            const link = document.createElement('a');
            link.download = 'read_across_diagram.png';
            link.href = dataUrl;
            link.click();
        } catch (err:any) {
            console.log(err);
        }

    }, []);

    // const downloadDiagramAsPNG = async () => {
    //     const reactFlowViewport = document.querySelector('.react-flow__viewport');

    //     if (!reactFlowViewport) return;

    //     const imageWidth = 768;
    //     const imageHeight = 768;    

    //     const nodeBounds = getNodesBounds(getNodes());
    //     const viewport = getViewportForBounds(nodeBounds, imageWidth, imageHeight, 0, 1, 1);

    //     try {
    //         const dataUrl = await toPng(reactFlowViewport as HTMLElement, { 
    //             // cacheBust: true,
    //             backgroundColor: '#ffffff',
    //             width: imageWidth,
    //             height: imageHeight,
    //             style: {
    //                 width: imageWidth.toString(),
    //                 height: imageHeight.toString(),
    //                 transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
    //             } 
    //         });

    //         const link = document.createElement('a');
    //         link.download = 'read_across_diagram.png';
    //         link.href = dataUrl;
    //         link.click();
    //     } catch (err:any) {
    //         console.log(err);
    //     }
    // }


    return (
        <div className="w-full h-full bg-white">
            {modalData && modalIndex !== null && (
                <RADetailsModal
                    data={modalData}
                    index={modalIndex}
                    selectedFilter={selectedFilter}
                    setSelectedFilter={setSelectedFilter}
                    searchInput={searchInput}
                    setSearchInput={setSearchInput}
                    open={modalOpen}
                    setOpen={setModalOpen}
                />
            )}
            <ReactFlow
                nodes={nodes}
                edges={edges}
                // onNodesChange={onNodesChange}
                // onEdgesChange={onEdgesChange}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                fitView
                nodesConnectable={false}
                style={{ backgroundColor: '#ffffff' }}
            >
                {/* <Background bgColor="ffffff"/> */}
                <Controls showInteractive={false} >
                    <ControlButton onClick={onDownloadPNG}>
                        <div><CameraIcon /></div>
                    </ControlButton>
                </Controls>
            </ReactFlow>
        </div>
    )
}